import { bookingTypes } from "@app/booking";
import { PaymentTypes, paymentTypes, paymentUtils } from "@app/payment";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetCheckoutBookingValidatableDependencyData } from "../hooks";

export interface ICheckoutBookingValidationProps {
    propertyId?: string;
    bookingId?: string;
}

export interface ICheckoutBookingValidationResult {
    revalidate: () => Promise<void>;
    startPolling: () => void;
    stopPolling: () => void;
    data?: {
        reservedBooking: bookingTypes.IReservedBooking;
        paymentIntentModel?: paymentTypes.TPaymentIntentModel;
    };
    state: {
        isPaymentAccepted: boolean;
        isPaymentPending: boolean;
        isPaymentDeclined: boolean;
        isPaymentIdle: boolean;
        isRevalidating: boolean;
    };
}

const DEFAULT_POLLING_INTERVAL_DELAY = 5000;
const DEFAULT_POLLING_TIMEOUT = 20000;

/**
 * useCheckoutBookingValidation
 * Checkout session booking and payment validation hook
 * @returns
 */
export const useCheckoutBookingValidation = ({
    bookingId,
    propertyId,
}: ICheckoutBookingValidationProps): ICheckoutBookingValidationResult => {
    const [isPolling, setIsPolling] = useState(false);

    const {
        data: checkoutBookingValidatableDependencyData,
        refetch: refetchCheckoutBookingValidatableDependencyData,
        isFetching: isCheckoutBookingValidatableDependencyDataFetching,
    } = useGetCheckoutBookingValidatableDependencyData(propertyId || "", bookingId || "", {
        enabled: !!propertyId && !!bookingId,
        refetchInterval: isPolling ? DEFAULT_POLLING_INTERVAL_DELAY : undefined,
        refetchOnWindowFocus: true,
    });

    const { reservedBooking, paymentIntentModel } = checkoutBookingValidatableDependencyData || {};
    const isPaymentAccepted = !!paymentIntentModel && paymentUtils.isPaymentAccepted(paymentIntentModel.status);
    const isPaymentPending = !!paymentIntentModel && paymentUtils.isPaymentPending(paymentIntentModel.status);
    const isPaymentIdle = !!paymentIntentModel && paymentUtils.isPaymentIdle(paymentIntentModel.status);
    const isPaymentDeclined = !!paymentIntentModel && !isPaymentAccepted && !isPaymentPending && !isPaymentIdle;
    const isRevalidating = isCheckoutBookingValidatableDependencyDataFetching;

    useEffect(() => {
        if (!isCheckoutBookingValidatableDependencyDataFetching) {
        }
    }, [isCheckoutBookingValidatableDependencyDataFetching]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        if (isPolling) {
            timeoutId = setTimeout(() => setIsPolling(false), DEFAULT_POLLING_TIMEOUT);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isPolling]);

    const revalidate = useCallback(async () => {
        await refetchCheckoutBookingValidatableDependencyData();
    }, [refetchCheckoutBookingValidatableDependencyData, isPolling]);

    const startPolling = useCallback(() => {
        setIsPolling(true);
    }, [setIsPolling]);

    const stopPolling = useCallback(() => {
        setIsPolling(false);
    }, [setIsPolling]);

    return useMemo(
        () => ({
            revalidate,
            startPolling,
            stopPolling,
            data: reservedBooking
                ? {
                      reservedBooking,
                      paymentIntentModel,
                  }
                : undefined,
            state: {
                isPaymentAccepted,
                isPaymentPending,
                isPaymentDeclined,
                isRevalidating,
                isPaymentIdle,
            },
        }),
        [revalidate, startPolling, reservedBooking, paymentIntentModel, isPaymentAccepted, isPaymentPending, isPaymentDeclined]
    );
};
